import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService: React.FC = () => {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl font-bold mb-6">Terms of Service</h1>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
        <p className="text-sm text-gray-700">
          <strong>Last Updated:</strong> November 17, 2025
        </p>
        <p className="text-sm text-gray-700 mt-1">
          <strong>Effective Date:</strong> November 17, 2025
        </p>
      </div>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
        <p className="mb-4">
          By accessing or using the RentSmart.ie website and services ("the
          Platform"), you agree to be legally bound by these Terms of Service
          ("Terms"). If you do not agree to these terms, you must not use our
          Platform.
        </p>
        <p className="mb-4">
          <strong>Important Notice:</strong> These Terms constitute a legally
          binding agreement between you and RentSmart.ie. Please read them
          carefully before using our services.
        </p>
        <p className="mb-4">
          By creating an account or using our Platform, you confirm that:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>You are at least 18 years of age</li>
          <li>You have the legal capacity to enter into binding contracts</li>
          <li>You will comply with all applicable laws and regulations</li>
          <li>All information you provide is accurate and up-to-date</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          2. Description of Service
        </h2>
        <p className="mb-4">
          RentSmart.ie is an online intermediary platform that connects property
          owners (landlords and agents) with individuals seeking rental
          accommodations (renters) in Ireland. Our services include:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            <strong>Property Listings:</strong> Tools for landlords to create,
            manage, and promote rental listings
          </li>
          <li>
            <strong>Search and Discovery:</strong> Advanced search functionality
            for renters to find suitable properties
          </li>
          <li>
            <strong>Communication:</strong> Secure messaging system for
            landlords and renters to connect
          </li>
          <li>
            <strong>Auto-Apply Feature:</strong> Optional automated application
            service for eligible renters
          </li>
          <li>
            <strong>Landlord Toolkit:</strong> Property management tools
            including rent tracking and tenant management
          </li>
          <li>
            <strong>Safety Features:</strong> Reporting mechanisms, identity
            verification, and content moderation
          </li>
        </ul>
        <p className="mb-4">
          <strong>Nature of Service:</strong> RentSmart.ie acts solely as an
          intermediary platform. We do not:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>Own, manage, or control any properties listed on the Platform</li>
          <li>Act as a landlord, tenant, or property agent</li>
          <li>Guarantee the accuracy of listings or user information</li>
          <li>Participate in rental transactions or agreements</li>
          <li>Provide legal, financial, or tenancy advice</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">3. User Accounts</h2>
        <h3 className="text-xl font-semibold mb-3">3.1 Account Creation</h3>
        <p className="mb-4">
          To access certain features of the Platform, you must create an
          account. You agree to:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            Provide accurate, current, and complete information during
            registration
          </li>
          <li>Maintain and promptly update your account information</li>
          <li>Maintain the security and confidentiality of your password</li>
          <li>
            Accept responsibility for all activities that occur under your
            account
          </li>
          <li>
            Notify us immediately of any unauthorized use or security breach
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          3.2 Account Types and Roles
        </h3>
        <p className="mb-4">
          RentSmart.ie offers three user roles, each with specific rights and
          responsibilities:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            <strong>Renters:</strong> Can search properties, save favorites,
            contact landlords, and apply for rentals
          </li>
          <li>
            <strong>Landlords:</strong> Can list properties, manage listings,
            respond to inquiries, and access landlord tools
          </li>
          <li>
            <strong>Property Agents:</strong> Can manage multiple properties on
            behalf of landlords, subject to PSRA licensing requirements
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          3.3 Verification Requirements
        </h3>
        <p className="mb-4">
          We may require identity verification for certain users or activities.
          Verification may include:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>Email verification (mandatory for all users)</li>
          <li>Phone number verification</li>
          <li>
            Identity document verification (passport, driving license, or
            national ID)
          </li>
          <li>PSRA license verification for property agents</li>
        </ul>
        <p className="mb-4">
          Failure to complete required verification may limit your access to
          Platform features.
        </p>

        <h3 className="text-xl font-semibold mb-3">3.4 Account Termination</h3>
        <p className="mb-4">
          You may close your account at any time through your account settings.
          We may suspend or terminate your account if:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>You violate these Terms or our Community Guidelines</li>
          <li>You engage in fraudulent, illegal, or harmful activity</li>
          <li>Your account remains inactive for more than 24 months</li>
          <li>Required by law or legal authority</li>
        </ul>
        <p className="mb-4">
          See Section 10 (Content Moderation and Enforcement) for details on our
          suspension and termination procedures.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          4. User Conduct and Prohibited Activities
        </h2>
        <h3 className="text-xl font-semibold mb-3">
          4.1 General Conduct Standards
        </h3>
        <p className="mb-4">
          You agree to use the Platform in accordance with all applicable laws
          and these Terms. You must:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>Treat all users with respect and professionalism</li>
          <li>Provide truthful and accurate information</li>
          <li>Honor commitments and agreements made through the Platform</li>
          <li>
            Comply with Irish landlord-tenant law and fair housing regulations
          </li>
          <li>
            Report suspected violations of these Terms or illegal activity
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          4.2 Prohibited Activities
        </h3>
        <p className="mb-4">
          The following activities are strictly prohibited and may result in
          immediate account suspension or termination:
        </p>
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <p className="font-semibold mb-2">Illegal Content and Activities:</p>
          <ul className="list-disc pl-6 mb-2">
            <li>Posting false, misleading, or fraudulent property listings</li>
            <li>Engaging in rental scams or fraud</li>
            <li>
              Money laundering or facilitating illegal financial transactions
            </li>
            <li>Violating housing discrimination laws</li>
            <li>Listing properties without proper authorization</li>
            <li>Evading rent control or tenancy protection regulations</li>
          </ul>
        </div>
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <p className="font-semibold mb-2">Harmful Behavior:</p>
          <ul className="list-disc pl-6 mb-2">
            <li>Harassment, threats, intimidation, or hate speech</li>
            <li>
              Discriminating against users based on protected characteristics
            </li>
            <li>
              Doxxing or sharing others' private information without consent
            </li>
            <li>Impersonating others or creating fake accounts</li>
            <li>
              Spamming, sending unsolicited messages, or engaging in aggressive
              marketing
            </li>
          </ul>
        </div>
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <p className="font-semibold mb-2">Platform Abuse:</p>
          <ul className="list-disc pl-6 mb-2">
            <li>
              Attempting to gain unauthorized access to accounts or systems
            </li>
            <li>Using automated tools (bots, scrapers) without permission</li>
            <li>Circumventing security measures or verification processes</li>
            <li>
              Interfering with the Platform's operation or other users' access
            </li>
            <li>
              Reverse engineering, copying, or extracting Platform code or
              content
            </li>
          </ul>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          5. Property Listings and Rental Agreements
        </h2>
        <h3 className="text-xl font-semibold mb-3">
          5.1 Landlord Responsibilities
        </h3>
        <p className="mb-4">
          If you list a property on RentSmart.ie, you represent and warrant
          that:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            You have the legal right and authorization to list the property
          </li>
          <li>All listing information is accurate, current, and complete</li>
          <li>
            The property meets all applicable safety and habitability standards
          </li>
          <li>
            You comply with all Irish landlord-tenant laws and regulations
          </li>
          <li>
            You are registered with the Residential Tenancies Board (RTB) if
            required
          </li>
          <li>All photos and descriptions accurately represent the property</li>
          <li>
            You disclose all material defects, limitations, or issues with the
            property
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          5.2 Platform Role and Limitations
        </h3>
        <p className="mb-4">
          <strong>Important:</strong> RentSmart.ie is an intermediary platform
          only. We do not:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            Verify the accuracy of property listings or user representations
          </li>
          <li>Inspect properties or enforce habitability standards</li>
          <li>Participate in or mediate rental agreements</li>
          <li>Handle rental payments or security deposits</li>
          <li>Act as a landlord, tenant, or property manager</li>
          <li>Provide legal, financial, or tenancy advice</li>
        </ul>
        <p className="mb-4">
          <strong>Rental Agreements:</strong> Any rental agreement is solely
          between the landlord and tenant. You are responsible for conducting
          your own due diligence, negotiating terms, and executing legally
          compliant rental agreements. We strongly recommend:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>Viewing properties in person before committing</li>
          <li>Verifying identities and credentials of all parties</li>
          <li>Using written rental agreements compliant with Irish law</li>
          <li>Registering tenancies with the RTB as required</li>
          <li>Seeking legal advice for complex situations</li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          5.3 Disputes and Liability
        </h3>
        <p className="mb-4">
          RentSmart.ie is not responsible for disputes arising from rental
          agreements, including but not limited to:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>Non-payment of rent or deposit disputes</li>
          <li>Property condition or maintenance issues</li>
          <li>Lease violations or early terminations</li>
          <li>Damages or losses incurred during tenancies</li>
          <li>Misrepresentations by landlords or tenants</li>
        </ul>
        <p className="mb-4">
          For tenancy disputes, you should contact the Residential Tenancies
          Board (RTB) or seek legal advice.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          6. Privacy and Data Protection
        </h2>
        <p className="mb-4">
          Your use of the Platform is governed by our{' '}
          <Link to="/privacy-policy" className="text-blue-600 hover:underline">
            Privacy Policy
          </Link>
          , which is incorporated into these Terms by reference. By using
          RentSmart.ie, you consent to our collection, use, and processing of
          your personal data as described in the Privacy Policy.
        </p>
        <p className="mb-4">
          <strong>Key Points:</strong>
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            We collect personal data necessary to provide our services (name,
            email, phone, property information)
          </li>
          <li>
            We process data in accordance with GDPR and Irish data protection
            law
          </li>
          <li>
            We implement automated data retention policies to comply with GDPR
            storage limitation principles
          </li>
          <li>
            You have rights to access, correct, delete, and export your personal
            data
          </li>
          <li>
            We use cookies and tracking technologies as described in our Cookie
            Policy
          </li>
          <li>We do not sell your personal data to third parties</li>
        </ul>
        <p className="mb-4">
          <strong>Data Processing Activities:</strong>
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            <strong>Identity Verification:</strong> We may process identity
            documents (passport, driving license) for verification purposes
          </li>
          <li>
            <strong>Content Moderation:</strong> We process reports and
            moderation decisions in accordance with DSA requirements
          </li>
          <li>
            <strong>Accessibility Data:</strong> We may collect information
            about accessibility features used to improve our services
          </li>
          <li>
            <strong>KYBC (Know Your Business Customer):</strong> We verify
            business credentials for property agents and landlords
          </li>
        </ul>
        <p className="mb-4">
          For full details on data processing, retention periods, and your
          rights, please review our
          <Link to="/privacy-policy" className="text-blue-600 hover:underline">
            {' '}
            Privacy Policy
          </Link>
          .
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          7. Intellectual Property Rights
        </h2>
        <h3 className="text-xl font-semibold mb-3">7.1 Platform Ownership</h3>
        <p className="mb-4">
          The RentSmart.ie Platform, including its design, software, graphics,
          logos, text, and compilation of content, is the exclusive property of
          RentSmart.ie and is protected by Irish and international copyright,
          trademark, and other intellectual property laws.
        </p>

        <h3 className="text-xl font-semibold mb-3">7.2 Limited License</h3>
        <p className="mb-4">
          We grant you a limited, non-exclusive, non-transferable, revocable
          license to access and use the Platform for personal, non-commercial
          purposes in accordance with these Terms. This license does not permit
          you to:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            Copy, modify, distribute, or create derivative works from the
            Platform
          </li>
          <li>
            Reverse engineer, decompile, or disassemble any part of the Platform
          </li>
          <li>
            Use automated tools (bots, scrapers) to extract data from the
            Platform
          </li>
          <li>Remove or alter copyright notices or trademarks</li>
          <li>
            Use the Platform for commercial purposes without our written consent
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          7.3 User-Generated Content
        </h3>
        <p className="mb-4">
          You retain ownership of content you submit to the Platform (listings,
          photos, descriptions, messages). By submitting content, you grant
          RentSmart.ie a worldwide, non-exclusive, royalty-free, transferable
          license to:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            Display, reproduce, and distribute your content on the Platform
          </li>
          <li>
            Create promotional materials featuring your content (with
            anonymization where appropriate)
          </li>
          <li>
            Use your content to improve our services and develop new features
          </li>
          <li>
            Store backup copies for disaster recovery and legal compliance
          </li>
        </ul>
        <p className="mb-4">
          <strong>Important:</strong> You are solely responsible for ensuring
          you have all necessary rights to the content you submit. You must not
          upload content that infringes others' intellectual property rights.
        </p>

        <h3 className="text-xl font-semibold mb-3">7.4 Trademark Notice</h3>
        <p className="mb-4">
          "RentSmart.ie" and associated logos are trademarks of RentSmart.ie.
          You may not use these trademarks without our prior written permission.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          8. Limitation of Liability and Disclaimers
        </h2>

        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
          <p className="font-semibold mb-2">⚠️ Important Legal Notice</p>
          <p className="text-sm">
            This section limits our liability. Please read carefully. These
            limitations are fair and reasonable under the Consumer Rights Act
            2022 and Irish law.
          </p>
        </div>

        <h3 className="text-xl font-semibold mb-3">
          8.1 Platform "As Is" Disclaimer
        </h3>
        <p className="mb-4">
          The Platform is provided on an "AS IS" and "AS AVAILABLE" basis
          without warranties of any kind, either express or implied, including
          but not limited to:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            Accuracy, completeness, or reliability of user-generated content
          </li>
          <li>Availability, uptime, or uninterrupted access to the Platform</li>
          <li>Freedom from errors, viruses, or other harmful components</li>
          <li>Fitness for a particular purpose or merchantability</li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          8.2 Limitation of Liability
        </h3>
        <p className="mb-4">
          To the maximum extent permitted by Irish law, RentSmart.ie shall not
          be liable for:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            <strong>Indirect or Consequential Damages:</strong> Including but
            not limited to loss of profits, data, goodwill, or business
            opportunities
          </li>
          <li>
            <strong>User Actions:</strong> Any losses resulting from
            interactions between users, rental transactions, or user-generated
            content
          </li>
          <li>
            <strong>Third-Party Actions:</strong> Fraud, scams,
            misrepresentations, or illegal activities by users
          </li>
          <li>
            <strong>Force Majeure:</strong> Events beyond our reasonable control
            (natural disasters, cyber attacks, government actions)
          </li>
          <li>
            <strong>Technical Issues:</strong> Service interruptions, data loss,
            or security breaches despite reasonable precautions
          </li>
        </ul>

        <p className="mb-4">
          <strong>Maximum Liability Cap:</strong> Our total liability for any
          claim arising from or related to these Terms or use of the Platform
          shall not exceed €100 or the amount you paid to RentSmart.ie in the 12
          months preceding the claim, whichever is greater.
        </p>

        <h3 className="text-xl font-semibold mb-3">
          8.3 Exceptions to Limitations (Consumer Rights Act 2022)
        </h3>
        <p className="mb-4">
          <strong>
            Nothing in these Terms excludes or limits our liability for:
          </strong>
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>Death or personal injury caused by our negligence</li>
          <li>Fraud or fraudulent misrepresentation by RentSmart.ie</li>
          <li>
            Breach of statutory rights that cannot be excluded under Irish law
          </li>
          <li>
            Any other liability that cannot be excluded or limited under
            applicable law
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">8.4 User Indemnification</h3>
        <p className="mb-4">
          You agree to indemnify, defend, and hold harmless RentSmart.ie, its
          officers, directors, employees, and agents from any claims, damages,
          losses, or expenses (including reasonable legal fees) arising from:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>Your violation of these Terms or applicable laws</li>
          <li>Your use of the Platform or interactions with other users</li>
          <li>Content you submit that infringes third-party rights</li>
          <li>Your rental transactions or disputes with other users</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">9. Changes to Terms</h2>
        <h3 className="text-xl font-semibold mb-3">
          9.1 Notification of Changes
        </h3>
        <p className="mb-4">
          We reserve the right to modify these Terms at any time, subject to the
          following notification requirements (in compliance with Consumer
          Rights Act 2022):
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            <strong>Material Changes:</strong> We will provide at least 30 days'
            advance notice via email and prominent Platform notice
          </li>
          <li>
            <strong>Minor Changes:</strong> Technical or administrative changes
            will be posted on this page with an updated "Last Modified" date
          </li>
          <li>
            <strong>Legal Requirements:</strong> Changes required by law may
            take effect immediately
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">9.2 Your Options</h3>
        <p className="mb-4">
          If you object to changes to these Terms, you may:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            Close your account before the changes take effect (no penalty)
          </li>
          <li>
            Contact us at{' '}
            <a
              href="mailto:legal@rentsmart.ie"
              className="text-blue-600 hover:underline"
            >
              legal@rentsmart.ie
            </a>{' '}
            to discuss your concerns
          </li>
        </ul>
        <p className="mb-4">
          Continued use of the Platform after changes take effect constitutes
          acceptance of the updated Terms.
        </p>

        <h3 className="text-xl font-semibold mb-3">9.3 Version History</h3>
        <p className="mb-4">
          We maintain a version history of these Terms. Previous versions are
          available upon request by emailing
          <a
            href="mailto:legal@rentsmart.ie"
            className="text-blue-600 hover:underline"
          >
            {' '}
            legal@rentsmart.ie
          </a>
          .
        </p>
      </section>

      {/* NEW SECTION 10: DSA Content Moderation */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          10. Content Moderation and Enforcement (DSA Compliance)
        </h2>

        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
          <p className="font-semibold mb-2">
            🇪🇺 Digital Services Act (DSA) Notice
          </p>
          <p className="text-sm">
            This section describes our content moderation policies in accordance
            with EU Regulation 2022/2065 (Digital Services Act). RentSmart.ie is
            committed to transparency, fairness, and user protection in our
            content moderation practices.
          </p>
        </div>

        <h3 className="text-xl font-semibold mb-3">
          10.1 Reporting Mechanisms
        </h3>
        <p className="mb-4">
          Users can report illegal content, terms violations, or harmful
          activity through:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            <strong>Report Button:</strong> Available on all property listings
          </li>
          <li>
            <strong>Report Form:</strong> Accessible at{' '}
            <Link
              to="/report-listing"
              className="text-blue-600 hover:underline"
            >
              /report-listing
            </Link>
          </li>
          <li>
            <strong>Email:</strong>{' '}
            <a
              href="mailto:abuse@rentsmart.ie"
              className="text-blue-600 hover:underline"
            >
              support@rentsmart.ie
            </a>
          </li>
          <li>
            <strong>In-App Messaging:</strong> Through our contact us page
          </li>
        </ul>
        <p className="mb-4">
          <strong>Report Categories (DSA-Compliant):</strong>
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            Illegal content (fraud, discrimination, housing law violations)
          </li>
          <li>Misleading or false information</li>
          <li>Scams or financial fraud</li>
          <li>Privacy violations</li>
          <li>Harassment or hate speech</li>
          <li>Inappropriate content</li>
          <li>Other terms violations</li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">10.2 Review Process</h3>
        <p className="mb-4">
          All reports are reviewed by our Trust & Safety team within:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            <strong>Urgent Reports:</strong> Illegal content, safety threats, or
            active scams reviewed within a reasonable timeframe (typically 24-72
            hours)
          </li>
          <li>
            <strong>Standard Reports:</strong> Terms violations reviewed within
            3-5 business days
          </li>
          <li>
            <strong>Complex Cases:</strong> May require additional investigation
            (up to 15 business days)
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">10.3 Enforcement Actions</h3>
        <p className="mb-4">
          Based on the severity and frequency of violations, we may take the
          following actions:
        </p>
        <div className="space-y-3 mb-4">
          <div className="border-l-4 border-yellow-500 pl-4">
            <p className="font-semibold">1. Warning</p>
            <p className="text-sm text-gray-700">
              First-time or minor violations receive a written warning
              explaining the issue
            </p>
          </div>
          <div className="border-l-4 border-orange-500 pl-4">
            <p className="font-semibold">2. Content Removal</p>
            <p className="text-sm text-gray-700">
              Violating listings, photos, or messages are removed with
              notification to the user
            </p>
          </div>
          <div className="border-l-4 border-red-500 pl-4">
            <p className="font-semibold">3. Feature Restrictions</p>
            <p className="text-sm text-gray-700">
              Temporary loss of posting, messaging, or other Platform features
              (7-30 days)
            </p>
          </div>
          <div className="border-l-4 border-red-600 pl-4">
            <p className="font-semibold">4. Account Suspension</p>
            <p className="text-sm text-gray-700">
              Temporary account suspension for serious or repeated violations
              (30-90 days)
            </p>
          </div>
          <div className="border-l-4 border-red-800 pl-4">
            <p className="font-semibold">5. Permanent Ban</p>
            <p className="text-sm text-gray-700">
              Permanent account termination for severe violations, illegal
              activity, or repeated offenses
            </p>
          </div>
        </div>

        <h3 className="text-xl font-semibold mb-3">
          10.4 Notification and Transparency
        </h3>
        <p className="mb-4">
          In accordance with DSA Article 17, we will notify you of enforcement
          actions with:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            <strong>Reason for Action:</strong> Clear explanation of which Terms
            or laws were violated
          </li>
          <li>
            <strong>Evidence:</strong> Description of content or behavior that
            triggered the action
          </li>
          <li>
            <strong>Legal Basis:</strong> Reference to specific Terms sections
            or applicable laws
          </li>
          <li>
            <strong>Appeal Rights:</strong> Information on how to appeal the
            decision (see Section 10.6)
          </li>
          <li>
            <strong>Timeframe:</strong> Duration of temporary restrictions or
            suspensions
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          10.5 Automated Moderation
        </h3>
        <p className="mb-4">
          We may use automated systems to detect potential violations (e.g.,
          image recognition, text analysis). However, all enforcement decisions
          that result in account suspension or permanent bans are reviewed by
          human moderators.
        </p>
        <p className="mb-4">
          <strong>Your Rights:</strong> You may request human review of any
          automated decision.
        </p>

        <h3 className="text-xl font-semibold mb-3">
          10.6 Appeals Process (DSA Article 20)
        </h3>
        <p className="mb-4">
          You have the right to appeal any content moderation decision. To file
          an appeal:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            <strong>Submit Appeal:</strong> Email{' '}
            <a
              href="mailto:appeals@rentsmart.ie"
              className="text-blue-600 hover:underline"
            >
              support@rentsmart.ie
            </a>{' '}
            within 30 days of the enforcement action
          </li>
          <li>
            <strong>Include Information:</strong> Your account details, the
            specific action you're appealing, and reasons for disagreement
          </li>
          <li>
            <strong>Review Timeline:</strong> We will review and respond to
            appeals within 15 business days
          </li>
          <li>
            <strong>Independent Review:</strong> For rejected appeals, you may
            request review by an independent out-of-court dispute settlement
            body
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          10.7 Frequent Illegal Content (DSA Article 23)
        </h3>
        <p className="mb-4">
          If you frequently post illegal content or violate these Terms, we may:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>Suspend your account temporarily or permanently</li>
          <li>Require additional verification before account reinstatement</li>
          <li>
            Report illegal activity to law enforcement or regulatory authorities
          </li>
          <li>Impose stricter content review on future submissions</li>
        </ul>
        <p className="mb-4">
          "Frequently" means three or more substantiated violations within a
          6-month period.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          11. Governing Law and Dispute Resolution
        </h2>
        <h3 className="text-xl font-semibold mb-3">11.1 Governing Law</h3>
        <p className="mb-4">
          These Terms are governed by and construed in accordance with the laws
          of Ireland. The application of the United Nations Convention on
          Contracts for the International Sale of Goods is expressly excluded.
        </p>

        <h3 className="text-xl font-semibold mb-3">11.2 Jurisdiction</h3>
        <p className="mb-4">
          Subject to Section 11.4 (Alternative Dispute Resolution), any disputes
          relating to these Terms shall be subject to the exclusive jurisdiction
          of the courts of Ireland.
        </p>

        <h3 className="text-xl font-semibold mb-3">11.3 EU Consumer Rights</h3>
        <p className="mb-4">
          If you are a consumer resident in the European Union:
        </p>
        <ul className="list-disc pl-8 mb-4">
          <li>
            Nothing in these Terms affects your statutory rights under EU
            consumer protection law
          </li>
          <li>
            You may bring proceedings in your country of residence if more
            favorable to you
          </li>
          <li>
            You have access to the EU Online Dispute Resolution platform at{' '}
            <a
              href="https://consumer-redress.ec.europa.eu/index_en"
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://consumer-redress.ec.europa.eu/index_en
            </a>
          </li>
        </ul>

        <h3 className="text-xl font-semibold mb-3">
          11.4 Alternative Dispute Resolution
        </h3>
        <p className="mb-4">
          Before pursuing legal action, we encourage you to contact us at{' '}
          <a
            href="mailto:legal@rentsmart.ie"
            className="text-blue-600 hover:underline"
          >
            support@rentsmart.ie
          </a>{' '}
          to resolve disputes informally. We commit to good faith efforts to
          resolve disputes within 30 days.
        </p>
        <p className="mb-4">
          <strong>Out-of-Court Dispute Settlement (DSA Article 21):</strong> For
          content moderation disputes, you may refer the matter to a certified
          out-of-court dispute settlement body. We will provide contact
          information for certified bodies upon request.
        </p>

        <h3 className="text-xl font-semibold mb-3">11.5 Class Action Waiver</h3>
        <p className="mb-4">
          <strong>Note:</strong> This waiver does not apply to consumers in
          jurisdictions where class action waivers are prohibited by law
          (including EU consumer residents).
        </p>
        <p className="mb-4">
          To the extent permitted by law, you agree that any dispute resolution
          proceedings will be conducted on an individual basis and not in a
          class, consolidated, or representative action.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          12. Contact Information and Regulatory Details
        </h2>

        <h3 className="text-xl font-semibold mb-3">12.1 Contact Details</h3>
        <p className="mb-4">
          If you have any questions about these Terms of Service, please contact
          us:
        </p>
        <div className="bg-gray-50 p-4 rounded-lg mb-4">
          <p className="mb-2">
            <strong>Legal Inquiries:</strong>{' '}
            <a
              href="mailto:legal@rentsmart.ie"
              className="text-blue-600 hover:underline"
            >
              support@rentsmart.ie
            </a>
          </p>
          <p className="mb-2">
            <strong>Content Moderation Appeals:</strong>{' '}
            <a
              href="mailto:appeals@rentsmart.ie"
              className="text-blue-600 hover:underline"
            >
              support@rentsmart.ie
            </a>
          </p>
          <p className="mb-2">
            <strong>Abuse Reports:</strong>{' '}
            <a
              href="mailto:abuse@rentsmart.ie"
              className="text-blue-600 hover:underline"
            >
              support@rentsmart.ie
            </a>
          </p>
          <p className="mb-2">
            <strong>General Support:</strong>{' '}
            <a
              href="mailto:support@rentsmart.ie"
              className="text-blue-600 hover:underline"
            >
              support@rentsmart.ie
            </a>
          </p>
          <p className="mb-2">
            <strong>Data Protection Officer:</strong>{' '}
            <a
              href="mailto:dpo@rentsmart.ie"
              className="text-blue-600 hover:underline"
            >
              support@rentsmart.ie
            </a>
          </p>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">
          13. Miscellaneous Provisions
        </h2>

        <h3 className="text-xl font-semibold mb-3">13.1 Entire Agreement</h3>
        <p className="mb-4">
          These Terms, together with our Privacy Policy, Cookie Policy, and
          Community Guidelines, constitute the entire agreement between you and
          RentSmart.ie regarding use of the Platform.
        </p>

        <h3 className="text-xl font-semibold mb-3">13.2 Severability</h3>
        <p className="mb-4">
          If any provision of these Terms is found to be unenforceable or
          invalid, that provision shall be limited or eliminated to the minimum
          extent necessary, and the remaining provisions shall remain in full
          force.
        </p>

        <h3 className="text-xl font-semibold mb-3">13.3 Waiver</h3>
        <p className="mb-4">
          Our failure to enforce any right or provision of these Terms shall not
          constitute a waiver of such right or provision unless acknowledged in
          writing by RentSmart.ie.
        </p>

        <h3 className="text-xl font-semibold mb-3">13.4 Assignment</h3>
        <p className="mb-4">
          You may not assign or transfer these Terms or your account without our
          prior written consent. We may assign these Terms in connection with a
          merger, acquisition, or sale of assets with 30 days' notice.
        </p>

        <h3 className="text-xl font-semibold mb-3">13.5 Force Majeure</h3>
        <p className="mb-4">
          RentSmart.ie shall not be liable for any failure or delay in
          performance due to circumstances beyond our reasonable control,
          including but not limited to acts of God, war, terrorism, riots,
          embargoes, acts of civil or military authorities, fire, floods,
          accidents, network infrastructure failures, strikes, or shortages of
          transportation, facilities, fuel, energy, labor, or materials.
        </p>

        <h3 className="text-xl font-semibold mb-3">13.6 Language</h3>
        <p className="mb-4">
          These Terms are provided in English. Any translations are provided for
          convenience only. In case of discrepancies, the English version shall
          prevail.
        </p>
      </section>

      {/* Related Documents */}
      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Related Documents</h2>
        <p className="mb-4">These Terms should be read in conjunction with:</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Link
            to="/privacy-policy"
            className="p-4 border border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
          >
            <h3 className="font-semibold text-blue-600 mb-2">Privacy Policy</h3>
            <p className="text-sm text-gray-600">
              How we collect, use, and protect your personal data
            </p>
          </Link>
          <Link
            to="/community-guidelines"
            className="p-4 border border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
          >
            <h3 className="font-semibold text-blue-600 mb-2">
              Community Guidelines
            </h3>
            <p className="text-sm text-gray-600">
              Standards of behavior and prohibited activities
            </p>
          </Link>
          <Link
            to="/cookie-policy"
            className="p-4 border border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
          >
            <h3 className="font-semibold text-blue-600 mb-2">Cookie Policy</h3>
            <p className="text-sm text-gray-600">
              How we use cookies and tracking technologies
            </p>
          </Link>
          <Link
            to="/safety"
            className="p-4 border border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
          >
            <h3 className="font-semibold text-blue-600 mb-2">Safety Center</h3>
            <p className="text-sm text-gray-600">
              Tips for safe rental transactions and reporting
            </p>
          </Link>
        </div>
      </section>

      {/* Acknowledgment */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-2">Acknowledgment</h3>
        <p className="text-gray-700">
          By using RentSmart.ie, you acknowledge that you have read, understood,
          and agree to be bound by these Terms of Service, including our content
          moderation policies and DSA compliance measures. If you do not agree
          to these Terms, you must not use our Platform.
        </p>
      </div>
    </div>
  );
};

export default TermsOfService;
